/**
 * Files linked to the wall where they are (plan 14), through the desktop
 * app's `linked_files.rs`. The app keeps its own list of links per account,
 * made only from files the OS granted this window (a drop or a pick) and
 * bound to the frame that shows it; the page names a link by its id and its
 * frame's, never by a path.
 */
import { invoke } from "@tauri-apps/api/core";
import { getUser } from "@/api/auth";
import { getPrivacyAccount } from "@/lib/privacy-consent";

/**
 * Why a linked file could not be read or opened: `unlinked` when this
 * computer has no link for the frame (another install, or a copied frame).
 */
export type LocalFileProblem =
  | "missing"
  | "unlinked"
  | "denied"
  | "too-large"
  | "not-pdf"
  | "failed";

export interface ThisComputer {
  machineId: string;
  machineName: string;
}

export interface LinkedFile {
  linkId: string;
  path: string;
  name: string;
  /** Null for a macOS package (a folder shown as one item). */
  sizeBytes: number | null;
}

/** One granted path: its link, or the error that kept it from one. */
export interface LinkOutcome {
  path: string;
  linked: LinkedFile | null;
  error: string | null;
}

let asked: Promise<string> | null = null;

/**
 * The account whose links these are: the one the app signed in (set by
 * `useAuth` in the windows that load the user, so it works offline), else
 * asked of the backend once per window (a pinned widget's).
 */
function accountId(): Promise<string> {
  const signedIn = getPrivacyAccount();
  if (typeof signedIn === "string") return Promise.resolve(signedIn);
  asked ??= getUser().then(
    (user) => String(user.id),
    (error: unknown) => {
      asked = null;
      throw error;
    },
  );
  return asked;
}

let computer: Promise<ThisComputer> | null = null;
let known: ThisComputer | null = null;

/** This computer as the frames it links name it. */
export function thisComputer(): Promise<ThisComputer> {
  computer ??= invoke<ThisComputer>("this_computer").then(
    (found) => (known = found),
    (error: unknown) => {
      computer = null;
      throw error;
    },
  );
  return computer;
}

/** This computer once `thisComputer` has said, else null (a menu's read). */
export function knownComputer(): ThisComputer | null {
  return known;
}

/** The problem a linked-file command reported. */
export function localFileProblem(error: unknown): LocalFileProblem {
  switch (String(error)) {
    case "missing":
      return "missing";
    case "unknown-link":
      return "unlinked";
    case "denied":
      return "denied";
    case "too-large":
      return "too-large";
    case "not-pdf":
      return "not-pdf";
    default:
      return "failed";
  }
}

/** Links the paths this window was granted, in order. */
export async function linkGrantedFiles(
  paths: readonly string[],
): Promise<LinkOutcome[]> {
  return invoke<LinkOutcome[]>("link_granted_files", {
    accountId: await accountId(),
    paths,
  });
}

/** Binds a new link to the frame made for it. */
export async function bindLinkedFile(
  linkId: string,
  frameId: string,
): Promise<void> {
  await invoke("bind_linked_file", {
    accountId: await accountId(),
    linkId,
    frameId,
  });
}

/** A link and the frame that shows it: what every read and open names. */
export interface LinkRef {
  linkId: string;
  frameId: string;
}

/**
 * Locate… or Link a file…: links `ref`'s frame to the granted `path` the
 * user picked: under its own link when this computer has it, else under a
 * fresh one (the returned `linkId`).
 */
export async function relinkLocalFile(
  ref: LinkRef,
  path: string,
): Promise<LinkedFile> {
  return invoke<LinkedFile>("relink_local_file", {
    accountId: await accountId(),
    ...ref,
    path,
  });
}

/** The linked PDF's bytes; rejects with the command's reason. */
export async function readLinkedPdf(ref: LinkRef): Promise<Uint8Array> {
  const bytes = await invoke<ArrayBuffer>("read_linked_pdf", {
    accountId: await accountId(),
    ...ref,
  });
  return new Uint8Array(bytes);
}

/** Opens the file in its default app, or shows what would run something. */
export async function openLocalFile(
  ref: LinkRef,
): Promise<"opened" | "revealed"> {
  return invoke<"opened" | "revealed">("open_local_file", {
    accountId: await accountId(),
    ...ref,
  });
}

/** Shows the file in Finder or Explorer. */
export async function revealLocalFile(ref: LinkRef): Promise<void> {
  await invoke("reveal_local_file", { accountId: await accountId(), ...ref });
}
